import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Modal, Button } from 'react-bootstrap';
import styledComponent from '../../../utils/styledComponent';
import theme from '../../../theme';
import StopBadge from '../../../components/atoms/StopBadge';
import SingleSelect from '../../../components/atoms/forms/SingleSelect';
import Textarea from '../../../components/atoms/forms/Textarea';
import { pluralize } from '../../../utils/displayFormat';
import { getStopRowKey, groupStopsByRoute } from '../RouteOperations/utils';
import {
  DISPATCH_NOTE_MIN_LENGTH,
  DISPATCH_REASON,
  DISPATCH_SUB_REASONS,
  dispatchNoteLength,
  dispatchSubReasonDescription,
  dispatchSubReasonLabel,
  isDispatchReasonComplete,
  requiresDispatchNote,
} from './dispatchReasons';

const STEP_REASON = 'reason';
const STEP_CONFIRM = 'confirm';

const SUB_REASON_OPTIONS = DISPATCH_SUB_REASONS.map(({ value, label }) => ({
  value,
  label,
}));

const StyledDiv = styledComponent('div', {
  '& .drm-intro': {
    fontSize: theme.typography.label.fontSize,
    color: theme.colors.textMuted,
    marginBottom: theme.spacing.fieldGap,
    lineHeight: theme.lineHeights.body,
  },
  '& .drm-field': {
    marginBottom: theme.spacing.fieldGap,
  },
  '& .drm-label': {
    ...theme.typography.label,
    color: theme.colors.text,
    marginBottom: theme.spacing.inputLabelGap,
  },
  '& .drm-reason-box': {
    background: theme.colors.dangerBg,
    border: `1px solid ${theme.colors.dangerBorder}`,
    borderRadius: theme.radii.input,
    padding: `${theme.spacing.gapLg} ${theme.spacing.gapXl}`,
  },
  '& .drm-reason-name': {
    fontSize: theme.typography.body.fontSize,
    fontWeight: theme.fontWeights.semibold,
    color: theme.colors.textDark,
  },
  '& .drm-hint': {
    fontSize: theme.typography.hint.fontSize,
    color: theme.colors.textMuted,
    marginTop: theme.spacing.labelGap,
    lineHeight: theme.lineHeights.body,
  },
  '& .drm-note-meta': {
    display: 'flex',
    justifyContent: 'space-between',
    gap: theme.spacing.gapMd,
    fontSize: theme.typography.hint.fontSize,
    color: theme.colors.textMuted,
    marginTop: theme.spacing.labelGap,
  },
  '& .drm-note-meta.is-invalid': {
    color: theme.colors.danger,
  },
  '& .drm-callout': {
    background: theme.colors.warningCalloutBg,
    border: `1px solid ${theme.colors.warningCalloutBorder}`,
    color: theme.colors.warningCalloutText,
    borderRadius: theme.radii.input,
    padding: `${theme.spacing.gapLg} ${theme.spacing.gapXl}`,
    fontSize: theme.typography.label.fontSize,
    lineHeight: theme.lineHeights.body,
    marginBottom: theme.spacing.fieldGap,
  },
  '& .drm-callout strong': {
    color: theme.colors.warningCalloutAccent,
  },
  '& .drm-group': {
    marginBottom: theme.spacing.fieldGap,
  },
  '& .drm-group-title': {
    display: 'flex',
    justifyContent: 'space-between',
    gap: theme.spacing.gapMd,
    fontSize: theme.typography.label.fontSize,
    fontWeight: theme.fontWeights.semibold,
    color: theme.colors.textDark,
    paddingBottom: theme.spacing.labelGap,
    borderBottom: `1px solid ${theme.colors.borderLight}`,
  },
  '& .drm-group-count': {
    color: theme.colors.textMuted,
    fontWeight: theme.fontWeights.normal,
  },
  '& .drm-row': {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.gapLg,
    padding: `${theme.spacing.gapMd} 0`,
    borderBottom: `1px solid ${theme.colors.borderLight}`,
    fontSize: theme.typography.body.fontSize,
    color: theme.colors.textDark,
  },
  '& .drm-row:last-of-type': {
    borderBottom: 'none',
  },
  '& .drm-row-name': {
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  '& .drm-row-id': {
    color: theme.colors.textMuted,
    fontSize: theme.sizes.smallFontSize,
  },
  '& .drm-warn': {
    fontSize: theme.typography.hint.fontSize,
    color: theme.colors.textMuted,
    lineHeight: theme.lineHeights.body,
  },
});

const StyledHeader = styledComponent('div', {
  display: 'contents',
  '& .drm-header': {
    justifyContent: 'center',
  },
  '& .drm-title': {
    textAlign: 'center',
  },
});

const StyledFooter = styledComponent('div', {
  display: 'contents',
  '& .drm-cancel': {
    background: theme.colors.dangerBg,
    borderColor: theme.colors.dangerBorder,
    color: theme.colors.danger,
  },
  '& .drm-cancel:hover:not(:disabled)': {
    background: theme.colors.dangerBorder,
    color: theme.colors.dangerHover,
  },
});

function DispatchReasonModal({
  show,
  selectedStops,
  isSubmitting,
  onConfirm,
  onCancel,
}) {
  const [step, setStep] = useState(STEP_REASON);
  const [subReason, setSubReason] = useState('');
  const [note, setNote] = useState('');

  useEffect(() => {
    if (show) {
      setStep(STEP_REASON);
      setSubReason('');
      setNote('');
    }
  }, [show]);

  const groups = useMemo(
    () => groupStopsByRoute(selectedStops),
    [selectedStops],
  );
  const stopCount = selectedStops.length;
  const stopsLabel = pluralize(stopCount, 'stop');
  const noteRequired = requiresDispatchNote(subReason);
  const noteLength = dispatchNoteLength(note);
  const complete = isDispatchReasonComplete({ subReason, note });
  const selectedOption =
    SUB_REASON_OPTIONS.find((o) => o.value === subReason) || null;
  const trimmedNote = note.trim();
  const isConfirmStep = step === STEP_CONFIRM;

  const handleHide = () => {
    if (!isSubmitting) onCancel();
  };

  const handleConfirm = () => {
    if (!complete || isSubmitting) return;
    onConfirm({ subReason, note: trimmedNote });
  };

  const renderReasonStep = () => (
    <>
      <div className="drm-intro">
        {`Applies one dispatch reason to the ${stopsLabel} across the ${pluralize(
          groups.length,
          'route',
        )} selected. Each stop is recorded individually as unreachable.`}
      </div>
      <div className="drm-field">
        <div className="drm-label">Reason</div>
        <div className="drm-reason-box" data-testid="dispatch-modal-reason">
          <div className="drm-reason-name">{DISPATCH_REASON.label}</div>
          <div className="drm-hint">{DISPATCH_REASON.description}</div>
        </div>
      </div>
      <div className="drm-field">
        <div className="drm-label">Sub-reason</div>
        <SingleSelect
          options={SUB_REASON_OPTIONS}
          value={selectedOption}
          onChange={(opt) => setSubReason(opt ? opt.value : '')}
          placeholder="— Select a sub-reason —"
          inputId="dispatch-sub-reason"
          isClearable
          isDisabled={isSubmitting}
          data-testid="dispatch-modal-sub-reason"
        />
        {subReason && (
          <div
            className="drm-hint"
            data-testid="dispatch-modal-sub-reason-desc"
          >
            {dispatchSubReasonDescription(subReason)}
          </div>
        )}
      </div>
      <div className="drm-field">
        <div className="drm-label">
          {noteRequired ? 'Additional context (required)' : 'Notes (optional)'}
        </div>
        <Textarea
          id="dispatch-note"
          rows={3}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder={
            noteRequired
              ? `Describe what happened (at least ${DISPATCH_NOTE_MIN_LENGTH} characters)`
              : 'Anything the team should know'
          }
          disabled={isSubmitting}
          dataTestId="dispatch-modal-note"
          aria-label={
            noteRequired ? 'Additional context (required)' : 'Notes (optional)'
          }
        />
        {noteRequired && (
          <div
            className={`drm-note-meta${
              noteLength < DISPATCH_NOTE_MIN_LENGTH ? ' is-invalid' : ''
            }`}
            data-testid="dispatch-modal-note-meta"
          >
            <span>
              {`Other needs a note of at least ${DISPATCH_NOTE_MIN_LENGTH} characters.`}
            </span>
            <span>{`${noteLength} / ${DISPATCH_NOTE_MIN_LENGTH}`}</span>
          </div>
        )}
      </div>
    </>
  );

  const renderConfirmStep = () => (
    <>
      <div className="drm-callout" data-testid="dispatch-modal-callout">
        <strong>
          {`${DISPATCH_REASON.label} · ${dispatchSubReasonLabel(subReason)}`}
        </strong>
        {trimmedNote && <div>{trimmedNote}</div>}
      </div>
      {groups.map(({ routeName, stops }) => (
        <div
          className="drm-group"
          key={routeName}
          data-testid={`dispatch-modal-group-${routeName}`}
        >
          <div className="drm-group-title">
            <span>{routeName}</span>
            <span className="drm-group-count">
              {pluralize(stops.length, 'stop')}
            </span>
          </div>
          {stops.map((stop) => {
            const stopKey = getStopRowKey(stop);
            const name = stop.displayName || stop.id;
            return (
              <div
                className="drm-row"
                key={stopKey}
                data-testid={`dispatch-modal-row-${stopKey}`}
              >
                <StopBadge tone="muted">{stop.stop}</StopBadge>
                <span className="drm-row-name" title={name}>
                  {name}
                </span>
                {stop.displayName && stop.displayName !== stop.id && (
                  <span className="drm-row-id">{stop.id}</span>
                )}
              </div>
            );
          })}
        </div>
      ))}
      <div className="drm-warn">
        Each stop will be marked unreachable, attributed to you. This cannot be
        undone from this page.
      </div>
    </>
  );

  return (
    <Modal show={show} onHide={handleHide} backdrop="static" size="lg">
      <StyledHeader>
        <Modal.Header className="drm-header">
          <Modal.Title className="drm-title">
            {isConfirmStep
              ? 'Confirm dispatch update'
              : `Mark ${stopsLabel} unreachable`}
          </Modal.Title>
        </Modal.Header>
      </StyledHeader>
      <Modal.Body>
        <StyledDiv>
          {isConfirmStep ? renderConfirmStep() : renderReasonStep()}
        </StyledDiv>
      </Modal.Body>
      <Modal.Footer>
        {isConfirmStep ? (
          <StyledFooter>
            <Button
              onClick={() => setStep(STEP_REASON)}
              disabled={isSubmitting}
              className="drm-cancel"
              data-testid="dispatch-modal-back"
            >
              Back
            </Button>
            <Button
              onClick={handleConfirm}
              disabled={!complete || isSubmitting}
              className="btn-success"
              data-testid="dispatch-modal-confirm"
            >
              {isSubmitting
                ? 'Marking…'
                : `Confirm — mark ${stopsLabel} unreachable`}
            </Button>
          </StyledFooter>
        ) : (
          <StyledFooter>
            <Button
              onClick={handleHide}
              disabled={isSubmitting}
              className="drm-cancel"
              data-testid="dispatch-modal-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={() => setStep(STEP_CONFIRM)}
              disabled={!complete || isSubmitting}
              className="btn-success"
              data-testid="dispatch-modal-review"
            >
              {`Review ${stopsLabel}`}
            </Button>
          </StyledFooter>
        )}
      </Modal.Footer>
    </Modal>
  );
}

DispatchReasonModal.propTypes = {
  show: PropTypes.bool.isRequired,
  selectedStops: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      route: PropTypes.string,
      stop: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      stockingIndex: PropTypes.number,
      displayName: PropTypes.string,
    }),
  ).isRequired,
  isSubmitting: PropTypes.bool,
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

DispatchReasonModal.defaultProps = {
  isSubmitting: false,
};

export default DispatchReasonModal;
